import React, { createContext, useContext, useState, useEffect } from 'react';
import { GoogleDriveService } from '../services/googleDriveService';

interface AuthContextType {
  isAuthenticated: boolean;
  user: any;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.onload = () => {
      window.gapi.load('client:auth2', {
        callback: initClient,
        onerror: (error: Error) => {
          console.error('Error loading GAPI client:', error);
        }
      });
    };
    document.body.appendChild(script);
  }, []);

  const initClient = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.error('Google Client ID is not configured');
      return;
    }

    window.gapi.client
      .init({
        clientId: clientId as string, // Type assertion since we checked it's not undefined
        scope: [
          'https://www.googleapis.com/auth/drive.file',
          'https://www.googleapis.com/auth/drive.appdata',
          'email',
          'profile'
        ].join(' ')
      })
      .then(() => {
        if (window.gapi.auth2.getAuthInstance().isSignedIn.get()) {
          handleAuthChange(true);
        }
        window.gapi.auth2.getAuthInstance().isSignedIn.listen(handleAuthChange);
      })
      .catch(error => {
        console.error('Error initializing Google API client:', error);
      });
  };

  const handleAuthChange = (isSignedIn: boolean) => {
    if (isSignedIn) {
      const googleUser = window.gapi.auth2.getAuthInstance().currentUser.get();
      const profile = googleUser.getBasicProfile();
      const accessToken = googleUser.getAuthResponse().access_token;

      setUser({
        id: profile.getId(),
        name: profile.getName(),
        email: profile.getEmail(),
        imageUrl: profile.getImageUrl(),
      });

      GoogleDriveService.getInstance().setAccessToken(accessToken);
      setIsAuthenticated(true);
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const signIn = async () => {
    try {
      await window.gapi.auth2.getAuthInstance().signIn();
    } catch (error) {
      console.error('Error signing in:', error);
    }
  };

  const signOut = async () => {
    try {
      await window.gapi.auth2.getAuthInstance().signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
